import {
  Component,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FilterMetadata, SelectItem } from '@openng/optimus-ui/api';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { MOC_EVENT_DETAILS_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { MOCEventSearch } from 'src/app/model/crm/moc-event-search';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MocEventApiService } from 'src/app/services/api/moc-event-api.service';
import { MocEventBuilderService } from 'src/app/services/builder/moc-event-builder.service';
import { MocEventTableService } from 'src/app/services/table/moc-event-table.service';
import { getMOCEventColumns } from '../../definitions/moc-event-table-definition';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { MOCEventType } from 'src/app/model/crm/moc-event-type';
import { MOCEventCategory } from 'src/app/model/crm/moc-event-category';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';

@Component({
  selector: 'app-moc-event-search-container',
  templateUrl: './moc-event-search-container.component.html',
  styleUrls: ['./moc-event-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class MocEventSearchContainerComponent
  extends SearchContainerComponent<MOCEventSearch>
  implements OnInit
{
  protected _logAndMessage = inject(LogAndMessageService);
  protected _mocApi = inject(MocEventApiService);
  protected _mocBuilder = inject(MocEventBuilderService);
  protected _mocTableService = inject(MocEventTableService);
  protected _translateService = inject(TranslateService);
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  eventTypes: SelectItem[] = [];
  eventCategories: SelectItem[] = [];
  canEdit = false;
  canCreate = false;

  mocEvents: MOCEventSearch[] = [];
  showSuccessMessage = true;
  esQuery = null;
  esSort = null;
  activeTabChangedSub?: Subscription;
  req: any = null;
  event: any = null;
  query: any = null;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._mocTableService);
    this.setupActiveTabChangedSubscription();
    this._translateService.get('CRM.SCREEN.MOC_EVENTS').subscribe((label) => {
      this._tabService.updateActiveTabLabel(label);
      this.screenName = label;
    });
    this.loadDropDowns();
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.CREATE_MOC_EVENTS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.EDIT_MOC_EVENTS,
    ]);
    this.columns = getMOCEventColumns(this.eventTypes, this.eventCategories);
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query: any = {
      title: null,
      eventType: null,
      eventCategories: null,
      eventStatus: null,
      description: null,
      sapOutageNumber: null,
      aor: null,
      acknowledged: null,
      startDt: null,
      endDt: null,
      createdAt: null,
      businessUnits: null,
    };

    if (filters.title) {
      query.title = (filters.title as FilterMetadata).value;
    }
    if (filters.eventType) {
      const etq = this.eventTypes.find(
        (et) => et.value === (filters.eventType as FilterMetadata).value
      );
      if (etq) {
        query.eventType = etq.label;
      }
    }

    if (filters.eventCategories) {
      const ecq = this.eventCategories.find(
        (ec) => ec.value === (filters.eventCategories as FilterMetadata).value
      );
      if (ecq) {
        query.eventCategories = ecq.label;
      }
    }

    if (
      filters.acknowledged &&
      (filters.acknowledged as FilterMetadata).value
    ) {
      if ((filters.acknowledged as FilterMetadata).value) {
        query.acknowledged = 'true';
      } else {
        query.acknowledged = 'false';
      }
    }

    if (filters.eventStatus) {
      query.eventStatus = (filters.eventStatus as FilterMetadata).value;
    }
    if (filters.description) {
      query.description = (filters.description as FilterMetadata).value;
    }
    if (filters.aors) {
      const aor = (filters.aors as FilterMetadata).value;
      query.aor = aor.indexOf('%') >= 0 ? aor : '%' + aor;
    }
    if (filters.sapOutageNumber) {
      query.sapOutageNumber = (filters.sapOutageNumber as FilterMetadata).value;
    }
    if (filters.startDt) {
      query.startDt = (filters.startDt as FilterMetadata).value;
    }
    if (filters.endDt) {
      query.endDt = (filters.endDt as FilterMetadata).value;
    }
    if (filters.createdAt) {
      query.createdAt = (filters.createdAt as FilterMetadata).value;
    }
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    query.businessUnits = selectedBusinessUnits;

    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._mocTableService.getLastLazyLoad()! });
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'createdAt', 'DESC');
    this.req = req;
    this.event = event;
    this.query = query;

    this._mocApi
      .queryForMOCEvents(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (mocEventConnection) => {
          this.mocEvents = mocEventConnection.items;
          this.totalRecords = mocEventConnection.totalRecords;
          this.elements = [...this.mocEvents];
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.GET_MOC_EVENTS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          this.loading = false;
        },
        complete: () => {
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'CRM.MESSAGES.SUCCESS.GET_MOC_EVENTS',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
          this.loading = false;
        },
      });
  }

  newMocEvent(): void {
    const mocEvent = this._mocBuilder.buildNullMocEvent();
    this._mocTableService.setSelected(mocEvent as unknown as any);
    const tab = this._tabService.buildNewTab(MOC_EVENT_DETAILS_CONTAINER, true);
    this._tabService.openTab(tab);
  }

  changeViewEvent($event: { data: MOCEventSearch }) {
    const tab = this._tabService.buildNewTab(
      MOC_EVENT_DETAILS_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    this._tabService.openTab(tab);
  }

  refresh(setLoading = true): void {
    this.lazyLoad({ lazy: this.tableService.getLastLazyLoad()! }, setLoading);
  }

  loadDropDowns(): void {
    // Load MOCEventTypes
    this._mocApi.getMOCEventTypes().subscribe((types) => {
      this.eventTypes = [
        { label: 'Choose', value: null },
        ...types
          .filter((et) => et.enabled === true)
          .map((et) => ({ label: et.name, value: et.id })),
      ];
      // After we load in the types, we need to re-set the columns so the options
      // get applied to the type filter dropdown
      this.columns = getMOCEventColumns(this.eventTypes, this.eventCategories);
    });

    // Load MOCEventCategories
    this._mocApi.getMOCEventCategories().subscribe((categoryConnection) => {
      this.eventCategories = [
        { label: 'Choose', value: null },
        ...categoryConnection.items
          .filter((ec) => ec.enabled === true)
          .map((ec) => ({ label: ec.name, value: ec.id })),
      ];
      // After we load in the categories, we need to re-set the columns so the options
      // get applied to the category filter dropdown
      this.columns = getMOCEventColumns(this.eventTypes, this.eventCategories);
    });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
